import { useCallback } from "react";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import {
  BMS_FAULTS_META_INFO_FIELDS,
  withMetaInfoFields,
} from "../../../Helper/Analytics/tableMetaInfo";

/**
 * Resolves a BMS Faults analytics table's columns from the bmsAlertV2 org meta,
 * falling back to the hardcoded chartConfig when the org has not configured it.
 *
 * The page already fetches its meta as `bmsAlertV2` (BmsFaults.jsx), so the
 * config is in context; only these tables were still reading chartConfig alone.
 * The table keys mirror PlatformAlertsV2 so the same org meta shape works on
 * both modules, except that BMS says Fault where Smart Alerts says Alert:
 *
 *   severitySummaryTable                      severityVehiclesTable
 *   severityDetailsPageTable                  autoResolvedSummaryTable
 *   autoResolvedVehiclesTable                 autoResolvedDetailsPageTable
 *   triggeredFaultBreakdownSummaryTable       triggeredFaultBreakdownVehiclesTable
 *   resolutionDurationBreakdownSummaryTable   resolutionDurationBreakdownVehiclesTable
 *   mostCriticalVehiclesTable
 *
 * Org meta overrides only the columns. Everything else the chartConfig entry
 * carries - titles, empty messages, transforms - is left alone, so configuring
 * columns cannot accidentally blank a heading.
 */
export const useBmsTableConfig = () => {
  const pagesContent = usePagesContent();
  const tables = pagesContent?.metaInfo?.table;

  /**
   * @param tableKey  key under bmsAlertV2.table
   * @param fallback  the chartConfig entry, used when the org has no config
   * @returns { tableColumnsConfig, tableColumnOrder, searchBar, isConfigured }
   */
  return useCallback(
    (tableKey, fallback) => {
      const meta = tables?.[tableKey];
      if (!meta?.columnsConfig) {
        return {
          tableColumnsConfig: fallback?.tableColumnsConfig,
          tableColumnOrder: fallback?.tableColumnOrder,
          searchBar: fallback?.searchBar,
          isConfigured: false,
        };
      }
      return {
        tableColumnsConfig: meta.columnsConfig,
        tableColumnOrder: meta.columnOrdering || fallback?.tableColumnOrder,
        searchBar: meta.searchBar ?? fallback?.searchBar,
        isConfigured: true,
      };
    },
    [tables]
  );
};

/**
 * The metaInfo a request should ask for once a table's columns are resolved:
 * whatever it already asked for, plus the entity attributes those columns need.
 */
export const useBmsMetaInfoFields = () => {
  const resolveTable = useBmsTableConfig();
  return useCallback(
    (existingFields, tableKey, fallback) =>
      withMetaInfoFields(
        existingFields,
        resolveTable(tableKey, fallback).tableColumnsConfig,
        BMS_FAULTS_META_INFO_FIELDS
      ),
    [resolveTable]
  );
};

/**
 * Whether the page's org meta has settled, either loaded or failed.
 *
 * The analytics tables must not fetch before it has. The org meta decides which
 * metaInfo the request asks for, so a fetch that beats it asks for nothing and
 * paints a screen of em dashes before repainting when the real request returns.
 * Set on both the success and failure paths, so this cannot wait forever.
 */
export const useBmsMetaReady = () => {
  const pagesContent = usePagesContent();
  return Boolean(pagesContent?.mounted);
};
